import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/lib/supabase'
import Button from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'
import { Sparkles } from 'lucide-react'

export default function DoneScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { profile, user, refreshProfile } = useAuth()
  const [visible, setVisible] = useState(false)

  const displayName = profile?.display_name || t('onboarding.reader_fallback')

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  // Mark onboarding complete. Without this, onboarded_at stays null and every future
  // login is treated as a new user, re-triggering onboarding. Idempotent: only writes
  // if not already set. Also starts the 30-day trial for first-time onboarders.
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    ;(async () => {
      try {
        if (profile?.onboarded_at) return // already done
        if (cancelled) return

        // Start trial for new users
        if (!profile?.trial_ends_at) {
          await supabase.rpc('start_trial', { p_user_id: user.id })
        }

        if (cancelled) return
        await supabase
          .from('profiles')
          .update({ onboarded_at: new Date().toISOString() })
          .eq('id', user.id)
          .is('onboarded_at', null) // don't overwrite an existing timestamp

        // Refresh the context profile so AuthGate sees the user as onboarded
        if (cancelled) return
        await refreshProfile()
      } catch (e) {
        console.warn('Could not complete onboarding:', e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id, profile?.onboarded_at, profile?.trial_ends_at, refreshProfile])

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-180px)] py-8 text-center">
      {/* Cherry mark */}
      <div
        className={`mb-6 transition-all duration-700 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
      >
        <img
          src="/brand/cherry-books-mark.svg"
          alt=""
          className="w-44 h-auto mx-auto"
        />
      </div>

      {/* Welcome heading */}
      <h1
        className={`font-display text-2xl text-ink mb-3 transition-all duration-700 delay-100 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.done_welcome', { name: displayName })}
      </h1>

      {/* Body text */}
      <p
        className={`font-body text-ink-muted leading-relaxed max-w-sm mb-8 transition-all duration-700 delay-200 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.done_body')}
      </p>

      {/* Cherry gift badge */}
      <div
        className={`inline-flex items-center gap-2 px-4 py-2 bg-terra/10 rounded-full mb-2 transition-all duration-700 delay-300 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <Sparkles className="w-4 h-4 text-terra" />
        <span className="font-body text-sm text-terra font-medium">
          {t('onboarding.done_gift_label', { count: 100 })}
        </span>
      </div>

      {/* Cherry explanation */}
      <p
        className={`font-body text-sm text-ink-muted mb-8 transition-all duration-700 delay-350 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.done_cherry_explanation')}
      </p>

      {/* CTA button */}
      <div
        className={`transition-all duration-700 delay-400 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <Button
          onClick={() => navigate('/home')}
          className="h-12 px-8 btn-primary"
        >
          {t('onboarding.done_cta')}
          <span className="ml-2">&rarr;</span>
        </Button>
      </div>
    </div>
  )
}
